import { AnchorButton, Icon, PopoverPosition, Tooltip } from '@blueprintjs/core'
import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import config from '../config'
import Channels from '../constants/Channels'
import { AppState } from '../store'

const ExternalChannelLink = ({
  channel,
  id = null,
  asin = null,
  listingUrl = null,
  productId = null,
  showCustomLink = false
}) => {
  // We first calculate this because we can use it to short-circuit the other calculations if it's false
  const isVisible = useMemo(() => {
    if (!channel) return false
    if (!id && !asin) return false
    return true
  }, [channel, id, asin])

  const country = useSelector((s: AppState) => s.current.organization.address.country)

  // We use useMemo to avoid unnecessary recalculations of these boolean values on every render.
  // If isVisible is false, we don't need to calculate these values, so we short-circuit the calculations.
  const isShopify = useMemo(() => isVisible && channel === Channels.SHOPIFY.name, [channel, isVisible])
  const isEbay = useMemo(() => isVisible && channel === Channels.EBAY.name, [channel, isVisible])
  const isShopgoodwill = useMemo(() => isVisible && channel === Channels.SHOPGOODWILL.name, [channel, isVisible])
  const isGoodwillFinds = useMemo(() => isVisible && channel === Channels.GOODWILLFINDS.name, [channel, isVisible])
  const isEtsy = useMemo(() => isVisible && channel === Channels.ETSY.name, [channel, isVisible])
  const isAmazon = useMemo(() => isVisible && channel === Channels.AMAZON.name, [channel, isVisible])

  const href = useMemo(() => {
    // If the link is not visible, we don't need to calculate the href
    if (!isVisible) {
      return null
    }

    if (isEbay) {
      const tld = country === 'CA' ? 'ca' : 'com'
      return Channels.EBAY.itemLink(id, tld)
    }

    if (isEtsy) {
      return `${config.MARKET_URLS[channel] + id}`
    }

    if (isGoodwillFinds) {
      return `${config.MARKET_URLS[channel] + id}.html`
    }

    if (isAmazon) {
      return asin ? `${config.MARKET_URLS[channel]}${asin}` : null
    }

    if (isShopify) {
      if (listingUrl !== 'UNKNOWN') {
        return listingUrl ? `//${listingUrl}` : null
      }

      if (productId) {
        return `/products/${productId}` // See OrderItem.shopify_listing_url for explanation. This is the best we can do.
      }

      return null
    }

    return config.MARKET_URLS[channel] + id
  }, [
    channel,
    id,
    asin,
    listingUrl,
    productId,
    isEbay,
    isShopify,
    isGoodwillFinds,
    isEtsy,
    isAmazon,
    country,
    isVisible
  ])

  const linkText = useMemo(() => {
    // If the link is not visible, we don't need to calculate the link text
    if (!isVisible) {
      return null
    }

    if (showCustomLink) {
      if (isEbay) {
        return 'View in eBay'
      }
      if (isShopify) {
        return 'View in Shopify'
      }
      if (isShopgoodwill) {
        return 'View in Shopgoodwill'
      }
      if (isGoodwillFinds) {
        return 'View in Goodwillfinds'
      }
      if (isEtsy) {
        return 'View in Etsy'
      }
      if (isAmazon) {
        return 'View on Amazon'
      }
    }

    if (isAmazon) {
      return 'View on Amazon'
    }

    if (isShopify) {
      if (listingUrl === 'UNKNOWN') {
        return 'See product for link' // See OrderItem.shopify_listing_url for explanation
      }
      return 'View on Shopify'
    }

    return id
  }, [showCustomLink, isEbay, isShopify, isShopgoodwill, isGoodwillFinds, isEtsy, isAmazon, listingUrl, id, isVisible])

  if (!isVisible) {
    return null
  }

  return (
    <Tooltip
      content="Ended listings are not viewable on Shopify"
      disabled={href || !isShopify}
      position={PopoverPosition.BOTTOM}
    >
      <AnchorButton
        minimal
        small
        intent="primary"
        target="_blank"
        rel="noopener noreferrer"
        disabled={!href}
        href={href}
        className="bp3-button-as-link"
        rightIcon={!showCustomLink && <Icon size={12} icon="share" />}
      >
        {linkText}
      </AnchorButton>
    </Tooltip>
  )
}

// workaround for Jest https://github.com/airbnb/enzyme/issues/1875

export { ExternalChannelLink }
export default React.memo(ExternalChannelLink)
